import * as React from "react";
import { connect } from "react-redux";
import get from "lodash/get";
import { validate } from "../../utils/validators";

import * as actions from "../../actions";
import {
  Checkbox as CheckboxComponent,
  CheckboxProps as CheckboxComponentProps,
} from "../../components/Checkbox/Checkbox";
import { ComponentConfig } from "../../components/Widget/Widget";
import { isEditMode } from "../../utils/sendClickToMerchant";
import { interpolate } from "../../utils/string";

interface CheckboxProps extends CheckboxComponentProps {
  setUserInput: typeof actions.setUserInput;
  onFormError: typeof actions.setWidgetFormError;
  name: string;
  checkedValue?: string;
  uncheckedValue?: string;
  validators: string[][];
}

interface CheckboxState {
  checked: boolean;
}

class Checkbox extends React.Component<CheckboxProps, CheckboxState> {
  constructor(props: CheckboxProps) {
    super(props);
    const values = this.getValues();

    this.state = {
      checked: values.isUnset
        ? values.defaultChecked
        : values.current === values.checkedValue,
    };
  }

  componentDidMount() {
    const { data, isEditing, name, setUserInput } = this.props;
    const values = this.getValues();

    // A checkbox that starts selected has to seed its value as well: the action
    // reads data[name], which is otherwise written only when it is changed.
    if (values.isUnset && values.defaultChecked) {
      setUserInput(name, interpolate(values.checkedValue, data, !isEditing));
    }
  }

  componentDidUpdate(prevProps: CheckboxProps) {
    if (this.props.isEditing) {
      const values = this.getValues(prevProps);

      if (
        values.current === values.previous &&
        this.state.checked !== (values.current === values.checkedValue)
      ) {
        this.setState({ checked: values.current === values.checkedValue });
      }
    }
  }

  getValues = (prevProps?: CheckboxProps) => {
    const { config, configName, data, name, isEditing } = this.props;
    const defaultToBlank = !isEditing;
    const values: any = {};
    const componentConfig = config[configName] as ComponentConfig;
    const checkedValue = componentConfig.checkedValue
      ? componentConfig.checkedValue
      : "yes";
    const rawValue = get(data, name);
    const value = interpolate(rawValue, data, defaultToBlank);
    values.current = value;
    values.checkedValue = checkedValue;
    values.isUnset =
      rawValue === undefined || rawValue === null || rawValue === "";
    values.defaultChecked = Boolean(componentConfig.defaultChecked);

    if (prevProps) {
      const prevRawValue = get(prevProps.data, prevProps.name);
      const prevValue = interpolate(
        prevRawValue,
        prevProps.data,
        defaultToBlank
      );
      values.previous = prevValue;
    }

    return values;
  };

  onChange = (checked: boolean) => {
    const { name, setUserInput, configName, config, data, isEditing } =
      this.props;
    const defaultToBlank = !isEditing;
    const componentConfig = config[configName] as ComponentConfig;
    const checkedValue = componentConfig.checkedValue || "yes";
    const uncheckedValue = componentConfig.uncheckedValue || "no";
    const rawValue = checked ? checkedValue : uncheckedValue;
    const value = interpolate(rawValue, data, defaultToBlank);

    setUserInput(name, value);
    this.validate(value);
    this.setState({ checked });
  };

  validate(value: string) {
    if (isEditMode()) {
      return;
    }

    const { name, onFormError, data, validators } = this.props;

    if (validators && onFormError) {
      // Checking for onFormError is a hot fix. We should determine if this logic is even
      // needed. Validating every time someone changes a checkbox value may not be necessary.
      const errorMessage = validate(validators, value, data);
      onFormError(name, errorMessage);
    }
  }

  render() {
    const { checked } = this.state;

    return (
      <CheckboxComponent
        {...this.props}
        checked={checked}
        onChange={this.onChange}
      />
    );
  }
}

export default connect(null, { setUserInput: actions.setUserInput })(Checkbox);
